//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Icon, ProgressBar } from 'unison';

import { VALIDATING_STATE, VALIDATION_FAILED_STATE, VALIDATION_STATUS } from '@apis/catalog-validation/catalogValidationModel';
import { ConnectPanelActions } from '@catalog-ingestion/actions/connectPanelActions';
import { GoogleCatalogIngestionModalActions } from '@catalog-ingestion/actions/googleCatalogIngestionModalActions';
import ValidationPanelErrorContent from '@catalog-ingestion/components/validation-panel/ValidationPanelErrorContent';
import { WORKFLOW_PANELS } from '@catalog-ingestion/constants/googleCatalogIngestionModalConstants';
import { MAX_TIME, ONE_MINUTE, STATUS_INTERVAL, VALIDATION_STEP_LABELS } from '@catalog-ingestion/constants/validationPanelConstants';
import { IValidationPanelState } from '@catalog-ingestion/models/validationPanelModel';
import { ICuralateStore } from '@models/AppState';
import styled from 'styled-components';
import { ReduxDispatch } from 'app/curalate.store';

function mapStateToProps(state: ICuralateStore) {
  return {
    validationStatus: state.catalogIngestionReducers.googleCatalogIngestionModal.validationStatus,
  };
}

const mapDispatchToProps = (dispatch: ReduxDispatch) => {
  return {
    actions: {
      modal: bindActionCreators(GoogleCatalogIngestionModalActions, dispatch),
      connectPanel: bindActionCreators(ConnectPanelActions, dispatch),
    },
  };
};

type Props = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;
export class ValidationPanel extends React.Component <Props, IValidationPanelState> {

  public state: IValidationPanelState = {
    statusIntervalId: undefined,
    processSecondsElapsed: 0,
  };

  public progressLabel = () => {
    switch (this.props.validationStatus) {
      case VALIDATION_STATUS.PROCESSING:
        return VALIDATION_STEP_LABELS.TWO;
      case VALIDATION_STATUS.COMPLETE:
        return (
          <ValidatedContainer>
            <Icon
              name='circle-checkmark'
              className='cu-pr-2 cu-color-success'
            />
            { VALIDATION_STEP_LABELS.THREE }
          </ValidatedContainer>
        );
      default:
        return VALIDATION_STEP_LABELS.ONE;
    }
  }

  public getLoadingMessage() {
    if (this.state.processSecondsElapsed < ONE_MINUTE) {
      return 'Hang tight! This usually takes about a minute.';
    } else {
      return 'It shouldn\'t be much longer!';
    }
  }

  public uploadProgress = () => {
    if (VALIDATING_STATE[this.props.validationStatus]) {
      // if the catalog is taking an unusually long time to load the progress bar shouldn't
      // appear as though it is complete. The large majority of catalogs won't take 4:30 minutes but if
      // they do, the progress bar will hang up at 90% loaded.
      if (this.state.processSecondsElapsed / MAX_TIME > 0.9) {
        return 0.9;
      } else {
        return (this.state.processSecondsElapsed / MAX_TIME);
      }
    } else if (this.props.validationStatus === VALIDATION_STATUS.COMPLETE) {
      return 1;
    } else {
      return 0;
    }
  }

  public checkValidationStatus() {
    const {
      validationStatus,
      actions: {
        modal: { requestValidationStatus, changePanel },
      }
    } = this.props;

    // this function gets called on an one second interval so the seconds elapsed get increased by 1
    this.setState( {processSecondsElapsed: this.state.processSecondsElapsed + 1} );

    if (validationStatus !== VALIDATION_STATUS.COMPLETE) {
      requestValidationStatus();
    } else {
      clearInterval(this.state.statusIntervalId);
      changePanel(WORKFLOW_PANELS.REVIEW_PANEL);
    }
  }

  public componentDidMount() {
    const { validationStatus } = this.props;
    if (validationStatus !== VALIDATION_STATUS.COMPLETE) {
      const intervalId = setInterval(this.checkValidationStatus.bind(this), STATUS_INTERVAL);
      this.setState({statusIntervalId: intervalId});
    }
  }

  public async closeValidationPanel() {
    if (!VALIDATION_FAILED_STATE[this.props.validationStatus]) {
      this.props.actions.modal.getValidationResults();
      await this.props.actions.modal.getInvalidSample();
      await this.props.actions.modal.getValidSample();
    }
    clearInterval(this.state.statusIntervalId);
  }

  public componentWillUnmount() {
    this.closeValidationPanel();
  }

  public render() {
    const currentUploadProgress = this.uploadProgress();
    if (VALIDATION_FAILED_STATE[this.props.validationStatus]) {
      clearInterval(this.state.statusIntervalId);
      return (
      <Sheet>
        <ValidationPanelErrorContent/>
      </Sheet>
      );
    } else {
      return (
        <>
          <div className='cu-text-subsection cu-mb-2'>
            { this.progressLabel() }
          </div>
          <ProgressBar
            isIndeterminate={ this.state.processSecondsElapsed > ONE_MINUTE &&
              VALIDATING_STATE[this.props.validationStatus] }
            value={ currentUploadProgress }
            className={ (currentUploadProgress >= 1) && 'cu-progress-bar-success' }
          />
          <div className='cu-text-body cu-mt-2'>
            { this.getLoadingMessage() }
          </div>
        </>
      );
    }
  }
}

const Sheet = styled.div.attrs({ className: 'cu-sheet-low' })`
  padding: ${({theme}) => theme.spacings[4]};
`;

const ValidatedContainer = styled.div`
  display: flex;
  align-items: center;
`;

export default connect(mapStateToProps, mapDispatchToProps)(ValidationPanel);
